import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/* =====================================================
   BuyData.js — Metisel Data Purchasing Component
   Import: import { BuyData } from "./BuyData";
   All provider requests go through the Metisel backend.
   Never expose the Nimohdollar API key in React.
===================================================== */

const API = {
  networks: "/api/buydata/fetch-networks",
  packages: "/api/buydata/fetch-data-packages",
  purchase: "/api/buydata/buy-data-package",
  status: "/api/buydata/fetch-single-transaction",
  history: "/api/buydata/history",
};

const REQ = { withCredentials: true, headers: { "Content-Type": "application/json", Accept: "application/json" } };

/* ===================== RESPONSE HELPERS ===================== */

const unwrap = (r) => r?.data?.data ?? r?.data ?? {};

const getList = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) if (Array.isArray(data?.[key])) return data[key];
  if (data?.data && data.data !== data) return getList(data.data, keys);
  return [];
};

const getError = (e) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || "Something went wrong. Please try again.";

const getNetworkId = (i) => i?.network_id ?? i?.networkId ?? i?.id ?? i?.network?.network_id ?? i?.network?.id;
const getNetworkName = (i) => {
  const raw = i?.network_name ?? i?.networkName ?? i?.network?.name ?? i?.name ?? `Network ${getNetworkId(i) ?? ""}`;
  // show the provider's AT Big Time network under one consistent label
  return String(raw).toLowerCase().replace(/[^a-z]/g, "").includes("bigtime") ? "AT Big Time" : raw;
};
/* Package fields: the provider's field names are not documented, so try the
   known names first, then fall back to matching on the key name. */
const isScalar = (v) => v != null && typeof v !== "object" && v !== "";

const BUNDLE_KEYS = ["shared_bundle", "sharedBundle", "bundle_size", "bundleSize", "volume", "size", "data_size", "bundle", "capacity", "data_volume", "data", "value"];
const PRICE_KEYS = ["sell_price", "price", "selling_price", "sellingPrice", "amount", "cost", "cost_price", "retail_price", "unit_price", "rate"];
const NAME_KEYS = ["package_name", "packageName", "name", "title", "label", "plan", "description"];

// Provider sizes are in GB unless stated otherwise: 2 / "2GB" -> 2000, "500MB" -> 500.
// Keys that already hold the raw shared_bundle value (MB) are kept as they are.
const RAW_BUNDLE_KEY = /^(shared_?bundle|bundle_?size|bundle)$/i;

const toBundle = (v, key = "") => {
  const m = String(v).trim().match(/^([\d.]+)\s*(gb|mb|g|m)?/i);
  if (!m) return undefined;
  let n = parseFloat(m[1]);
  const unit = (m[2] || (RAW_BUNDLE_KEY.test(key) || /mb/i.test(key) ? "mb" : "gb")).toLowerCase();
  if (unit === "gb" || unit === "g") n *= 1000;
  return n > 0 ? Math.round(n) : undefined;
};

const getBundle = (i) => {
  if (!i) return undefined;
  for (const k of BUNDLE_KEYS) if (isScalar(i[k])) { const b = toBundle(i[k], k); if (b) return b; }
  for (const [k, v] of Object.entries(i)) if (/bundle|volume|size|capacity|data|gb|mb/i.test(k) && isScalar(v)) { const b = toBundle(v, k); if (b) return b; }
  return undefined;
};

const isMoney = (v) => isScalar(v) && Number.isFinite(Number(v));

const getPrice = (i) => {
  if (!i) return undefined;
  for (const k of PRICE_KEYS) if (isMoney(i[k])) return i[k];
  for (const [k, v] of Object.entries(i)) if (/price|cost|amount|rate|fee/i.test(k) && isMoney(v)) return v;
  return undefined;
};

const getPackageName = (i) => {
  for (const k of NAME_KEYS) if (isScalar(i?.[k]) && !/^\d+$/.test(String(i[k]))) return i[k];
  const bundle = getBundle(i);
  if (!bundle) return "Data package";
  return bundle >= 1000 ? `${+(bundle / 1000).toFixed(2)} GB` : `${bundle} MB`;
};

const getTransactionId = (i) =>
  i?.orderId ?? i?.transaction?.orderId ??
  i?.transactionId ?? i?.transaction_code ?? i?.transaction_id ??
  i?.transaction?.transactionId ?? i?.transaction?.transaction_code ?? i?.transaction?.transaction_id ?? i?.transaction?.id ?? "";

const getStatus = (i) =>
  i?.status ?? i?.transaction?.status ?? i?.transaction_status ?? i?.transactionStatus ?? i?.transaction?.transaction_status ?? "Unknown";

const getRecipient = (i) => i?.recipientMsisdn ?? i?.recipient_msisdn ?? i?.beneficiary_number ?? i?.recipient ?? "";
const getNetwork = (i) =>
  i?.networkName ?? i?.network_name ?? i?.network?.name ?? i?.networkId ?? i?.network_id ?? i?.network ?? "—";
const getAmount = (i) => i?.amount ?? i?.price ?? i?.selling_price ?? i?.sellingPrice ?? null;
const getDate = (i) => i?.createdAt ?? i?.created_at ?? i?.timestamp ?? i?.date;
const getCurrency = (i) => i?.currency || i?.currency_code || "GHS";

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const formatMoney = (amount, currency = "GHS") => {
  if (amount == null || amount === "") return "Price unavailable";
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const networkRank = (n) => {
  const name = String(getNetworkName(n)).trim().toLowerCase();
  if (name === "mtn") return 0;
  if (name.startsWith("telecel") && !name.includes("afa")) return 1;
  if (name.replace(/[^a-z]/g, "").includes("bigtime")) return 2; // AT Big Time
  return -1;
};

// short label used on the network buttons
const networkLabel = (n) => ["MTN", "Telecel", "AT Big Time"][networkRank(n)] ?? getNetworkName(n);

// package card colours: MTN yellow, Telecel red, AT Big Time blue (same order as networkRank)
const NETWORK_THEME = [
  { card: "border-yellow-400 bg-yellow-400 text-neutral-900 hover:bg-yellow-300", price: "text-neutral-800", tab: "border-yellow-400 bg-yellow-400 text-neutral-900" },
  { card: "border-red-600 bg-red-600 text-white hover:bg-red-500", price: "text-red-50", tab: "border-red-600 bg-red-600 text-white" },
  { card: "border-blue-600 bg-blue-600 text-white hover:bg-blue-500", price: "text-blue-50", tab: "border-blue-600 bg-blue-600 text-white" },
];
const FALLBACK_THEME = { card: "border-orange-500 bg-orange-500 text-white hover:bg-orange-400", price: "text-orange-50", tab: "border-orange-500 bg-orange-500 text-white" };
const networkTheme = (n) => NETWORK_THEME[networkRank(n)] ?? FALLBACK_THEME;

/* ===================== PAYMENT (GHSX / USWD) ===================== */
/* Must match buyDataRoute.js: package prices are in GHS, 1 GHSX = GHS 1, 1 USWD = 12.14 GHSX. */

const GHS_PER_GHSX = 1;
const GHSX_PER_USWD = 12.14;

const ceil2 = (n) => Math.ceil(Number(n) * 100 - 1e-9) / 100;
const ghsxCost = (priceGhs) => ceil2(Number(priceGhs) / GHS_PER_GHSX);
const uswdCost = (priceGhs) => ceil2(ghsxCost(priceGhs) / GHSX_PER_USWD);

const PAY = { ghsx: { label: "GHSX", cost: ghsxCost }, uswd: { label: "USWD", cost: uswdCost } };

const getAvailable = (wallet) => {
  const v = Number(wallet?.available ?? wallet?.balance ?? 0);
  return Number.isFinite(v) ? v : 0;
};

/* Ghana numbers are entered as 10 digits starting with 0 (0551053716): +233 / 233 / 00233 is turned into 0 */
const normalizePhone = (value) => {
  let v = String(value ?? "").replace(/[^\d+]/g, "");
  if (v.startsWith("+233")) v = "0" + v.slice(4);
  else if (v.startsWith("00233")) v = "0" + v.slice(5);
  else if (v.startsWith("233")) v = "0" + v.slice(3);
  return v.replace(/\D/g, "").slice(0, 10);
};

const isValidPhone = (v) => /^0\d{9}$/.test(v);
const PHONE_ERROR = "Enter a valid 10-digit phone number starting with 0.";

const getPackageKey = (item, index) => `${getNetworkId(item)}:${getBundle(item)}:${index}`;

/* ===================== SMALL UI PIECES ===================== */

function TransactionStatus({ status }) {
  const value = String(status || "Unknown").trim().toLowerCase();

  let color = "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300";
  if (/success|complete|confirmed|delivered/.test(value)) color = "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
  else if (/fail|error|reject|cancel/.test(value)) color = "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  else if (/pending|process|placed|initiated|queued|unknown/.test(value)) color = "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${color}`}>
      {String(status || "Unknown").replaceAll("_", " ")}
    </span>
  );
}

const Field = ({ label, small, className = "", children }) => (
  <div>
    <dt className={`${small ? "text-xs " : ""}text-neutral-500`}>{label}</dt>
    <dd className={`mt-1 ${className}`}>{children}</dd>
  </div>
);

const cardClass = "rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800 md:p-5";
const inputClass =
  "w-full rounded-xl border border-neutral-300 bg-white px-3 py-3 text-sm text-neutral-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:ring-orange-900/30";
const buttonClass =
  "inline-flex h-11 items-center justify-center rounded-xl bg-orange-500 px-4 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60";
const smallBtnClass = "rounded-lg border border-neutral-300 px-3 py-2 text-sm hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800";

/* ===================== DATA STORE (load once) ===================== */
/* Call this once in the parent (Home.js) so networks, packages and history are fetched when the app loads
   and are not fetched again each time the Buy Data tab is opened. */

export function useBuyData(enabled = true) {
  const [networks, setNetworks] = useState([]);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingNetworks, setLoadingNetworks] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadNetworks = useCallback(async () => {
    setLoadingNetworks(true);
    try {
      const list = getList(unwrap(await axios.get(API.networks, { withCredentials: true })), ["networks", "results", "items"])
        .filter((n) => networkRank(n) >= 0)
        .sort((a, b) => networkRank(a) - networkRank(b));
      setNetworks(list);
      return "";
    } catch (err) {
      return `Could not load networks: ${getError(err)}`;
    } finally {
      setLoadingNetworks(false);
    }
  }, []);

  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      setPackages(getList(unwrap(await axios.get(API.packages, { withCredentials: true })), ["packages", "data_packages", "results", "items"]));
      return "";
    } catch (err) {
      return `Could not load data packages: ${getError(err)}`;
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      setHistory(getList(unwrap(await axios.get(API.history, { withCredentials: true })), ["history", "purchases", "transactions", "results", "items"]));
      setLoadError((cur) => (cur.startsWith("Could not load purchase history") ? "" : cur));
    } catch (err) {
      setLoadError(`Could not load purchase history: ${getError(err)}`);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoadError("");
    const [a, b] = await Promise.all([loadNetworks(), loadPackages(), loadHistory()]);
    setLoadError((cur) => a || b || cur);
  }, [loadNetworks, loadPackages, loadHistory]);

  // one load when enabled (e.g. the user is logged in); cleared again on logout
  useEffect(() => {
    if (enabled) reload();
    else { setNetworks([]); setPackages([]); setHistory([]); setLoadError(""); }
  }, [enabled, reload]);

  return { networks, packages, history, loadingNetworks, loadingPackages, loadingHistory, loadError, loadHistory, reload };
}

/* ===================== BUY DATA COMPONENT ===================== */

/* ===================== INPUT DRAFTS ===================== */
/* The numbers the user typed are kept (per user) so they are still there after leaving the page or reloading. */

const loadDraft = (key) => {
  try { return (key && JSON.parse(localStorage.getItem(key) || "{}")) || {}; } catch { return {}; }
};

const saveDraft = (key, draft) => {
  try { if (key) localStorage.setItem(key, JSON.stringify(draft)); } catch { /* storage unavailable */ }
};

export function BuyData({ onBack, ghsxWallet, uswdWallet, onSuccess, data, userId }) {
  const draftKey = userId ? `buydata-draft:${userId}` : null;
  const [draft] = useState(() => loadDraft(draftKey));

  // `data` comes from useBuyData() in the parent (Home.js), which loads once. Without it, load here.
  const ownStore = useBuyData(!data);
  const { networks, packages, history, loadingNetworks, loadingPackages, loadingHistory, loadError, loadHistory, reload } = data || ownStore;

  const [networkId, setNetworkId] = useState(""); // MTN is selected by default once networks load (they are sorted MTN first)
  const [packageKey, setPackageKey] = useState("");
  const [phone, setPhone] = useState(normalizePhone(draft.phone));
  const [payMethod, setPayMethod] = useState(PAY[draft.payMethod] ? draft.payMethod : "ghsx");
  const [buyView, setBuyView] = useState("packages"); // "packages" | "form" | "preview" | "done"
  const [summary, setSummary] = useState(null);

  const [statusPhone, setStatusPhone] = useState(normalizePhone(draft.statusPhone));
  const [transactionResult, setTransactionResult] = useState(null);

  const [activeTab, setActiveTab] = useState("buy");

  const [buying, setBuying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const clearNotices = () => { setError(""); setMessage(""); };

  // top-right menu (Transaction Status / Purchases)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  useEffect(() => { saveDraft(draftKey, { phone, statusPhone, payMethod }); }, [draftKey, phone, statusPhone, payMethod]);

  /* ---------- default network once the networks have loaded ---------- */

  useEffect(() => {
    if (!networks.length) { setNetworkId(""); return; }
    setNetworkId((cur) => (networks.some((i) => String(getNetworkId(i)) === String(cur)) ? cur : String(getNetworkId(networks[0]) ?? "")));
  }, [networks]);

  /* ---------- packages for the selected network ---------- */

  const filteredPackages = useMemo(
    () =>
      packages.filter((item) => {
        const id = item?.network_id ?? item?.networkId ?? item?.network?.network_id ?? item?.network?.id;
        return id != null && String(id) === String(networkId);
      }).sort((a, b) => (getBundle(a) || 0) - (getBundle(b) || 0)),
    [packages, networkId]
  );

  useEffect(() => {
    if (!filteredPackages.length) { setPackageKey(""); return; }
    setPackageKey((cur) =>
      filteredPackages.some((item, i) => getPackageKey(item, i) === cur) ? cur : getPackageKey(filteredPackages[0], 0)
    );
  }, [filteredPackages]);

  // network button: switch network and show its packages
  const selectNetwork = (id) => { clearNotices(); setNetworkId(String(id)); };

  // package button: choose the package and open the purchase form
  const choosePackage = (key) => { clearNotices(); setPackageKey(key); setBuyView("form"); };

  const selectedPackage = useMemo(() => {
    const index = filteredPackages.findIndex((item, i) => getPackageKey(item, i) === packageKey);
    return index >= 0 ? filteredPackages[index] : null;
  }, [filteredPackages, packageKey]);

  // colour of the selected network (MTN yellow, Telecel red, AT Big Time blue)
  const selectedTheme = networkTheme(networks.find((n) => String(getNetworkId(n)) === String(networkId)) ?? selectedPackage);

  /* ---------- payment amounts / balances ---------- */

  const priceGhs = Number(getPrice(selectedPackage));
  const hasPrice = Number.isFinite(priceGhs) && priceGhs > 0;
  const balances = { ghsx: getAvailable(ghsxWallet), uswd: getAvailable(uswdWallet) };
  const costs = { ghsx: hasPrice ? ghsxCost(priceGhs) : null, uswd: hasPrice ? uswdCost(priceGhs) : null };
  const insufficient = hasPrice && balances[payMethod] + 1e-9 < costs[payMethod];
  const insufficientMsg = `You have insufficient ${PAY[payMethod].label} balance to purchase this data.`;

  /* ---------- buy data package ---------- */

  // Step 1: validate the form and show the preview
  const handlePreview = (event) => {
    event.preventDefault();
    clearNotices();
    setTransactionResult(null);

    const recipient = normalizePhone(phone);
    if (!isValidPhone(recipient)) return setError(PHONE_ERROR);
    if (!selectedPackage) return setError("Select a valid data package.");

    const selectedNetworkId = Number(networkId);
    const sharedBundle = Number(getBundle(selectedPackage));

    if (!Number.isInteger(selectedNetworkId) || selectedNetworkId < 1 || !Number.isInteger(sharedBundle) || sharedBundle < 1) {
      return setError("The selected network or package has invalid information.");
    }

    if (!hasPrice) return setError("This package has no price, so it cannot be purchased.");
    if (insufficient) return setError(insufficientMsg);

    setSummary({
      recipient,
      networkId: selectedNetworkId,
      sharedBundle,
      networkName: getNetworkName(networks.find((n) => String(getNetworkId(n)) === String(networkId)) ?? selectedPackage),
      packageName: getPackageName(selectedPackage),
      priceGhs,
      method: payMethod,
      cost: costs[payMethod],
      balanceAfter: balances[payMethod] - costs[payMethod],
    });
    setBuyView("preview");
  };

  // Step 2: pay and place the order
  const handleConfirm = async () => {
    if (!summary) return;
    clearNotices();

    if (balances[summary.method] + 1e-9 < summary.cost) return setError(`You have insufficient ${PAY[summary.method].label} balance to purchase this data.`);

    setBuying(true);

    try {
      const response = await axios.post(
        API.purchase,
        { recipient_msisdn: summary.recipient, network_id: summary.networkId, shared_bundle: summary.sharedBundle, payment_method: summary.method },
        REQ
      );

      const data = unwrap(response);
      const txn = data?.transaction ?? data;
      const id = getTransactionId(txn);

      setStatusPhone(summary.recipient);
      setSummary((cur) => ({
        ...cur,
        transactionId: id,
        status: getStatus(txn),
        message: data?.message || (id ? `Order ID: ${id}` : "Purchase request submitted. Check its status before trying again."),
      }));
      setBuyView("done");
      onSuccess?.();
      await loadHistory();
    } catch (err) {
      setError(getError(err));
      onSuccess?.(); // balances may have changed (payment taken or refunded)
      // A timeout or server error does not necessarily mean the provider
      // rejected the purchase. Refresh history before attempting another one.
      await loadHistory();
    } finally {
      setBuying(false);
    }
  };

  // keeps the entered number as a draft so it is still there for the next purchase
  const startNewPurchase = () => { clearNotices(); setSummary(null); setBuyView("packages"); };

  // Back: preview -> form -> packages; Status/Purchases -> Buy Data; Buy Data packages -> leave the page
  const handleBack = () => {
    if (activeTab === "buy" && buyView === "preview") { clearNotices(); setBuyView("form"); }
    else if (activeTab === "buy" && buyView === "form") { clearNotices(); setBuyView("packages"); }
    else if (activeTab === "buy" && buyView === "done") startNewPurchase();
    else if (activeTab !== "buy") { clearNotices(); setActiveTab("buy"); } // status / purchases -> back to Buy Data
    else onBack?.();
  };

  /* ---------- check transaction status ---------- */

  const handleCheckStatus = async (event) => {
    event?.preventDefault();
    clearNotices();
    setTransactionResult(null);

    const recipient = normalizePhone(statusPhone);
    if (!isValidPhone(recipient)) return setError(PHONE_ERROR);

    setCheckingStatus(true);

    try {
      const data = unwrap(await axios.post(API.status, { recipient_msisdn: recipient }, REQ));
      setTransactionResult(data?.transaction ?? data);
      setMessage("Transaction status retrieved.");
      await loadHistory();
    } catch (err) {
      setError(getError(err));
    } finally {
      setCheckingStatus(false);
    }
  };

  /* ---------- render ---------- */

  const result = transactionResult;

  return (
    <div
      className="h-full min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-white text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div
        className={`mx-auto w-full p-4 md:p-8 ${activeTab === "buy" && (buyView === "packages" || buyView === "form") ? "max-w-6xl" : "max-w-3xl md:max-w-4xl"}`}
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
        }}
      >
        {/* BACK + TITLE + MENU (the menu is not shown on the preview page) */}
        <header className="mb-3 flex items-center gap-2">
          {onBack && (
            <button type="button" onClick={handleBack} aria-label="Go back" className="flex h-11 w-11 shrink-0 items-center justify-center text-neutral-700 hover:text-orange-500 dark:text-neutral-200 dark:hover:text-orange-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
          )}

          <h1 className="min-w-0 flex-1 truncate text-base font-bold">
            {activeTab === "status" ? "Transaction Status" : activeTab === "history" ? "Purchases" : "Buy Data"}
          </h1>

          {!(activeTab === "buy" && buyView === "preview") && (
            <div ref={menuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex h-11 w-11 items-center justify-center rounded-xl text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>

              {menuOpen && (
                <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-52 overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
                  {[["history", "Purchases"], ["status", "Transaction Status"]].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="menuitem"
                      onClick={() => { setActiveTab(value); clearNotices(); setMenuOpen(false); }}
                      className={`block w-full px-4 py-3 text-left text-sm font-medium hover:bg-neutral-100 dark:hover:bg-neutral-800 ${activeTab === value ? "text-orange-600 dark:text-orange-400" : ""}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </header>

        {/* MESSAGES */}
        {!error && loadError && (
          <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            <span>{loadError}</span>
            <button type="button" onClick={reload} className="shrink-0 font-semibold underline">Retry</button>
          </div>
        )}
        {error && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</div>}
        {message && <div role="status" className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300">{message}</div>}

        {/* ================= BUY DATA TAB: NETWORK + PACKAGES ================= */}
        {activeTab === "buy" && buyView === "packages" && (
          <section className={cardClass}>
            <h2 className="text-base font-bold">Select Network</h2>

            <div role="tablist" aria-label="Network" className="mt-3 grid grid-cols-3 gap-2">
              {loadingNetworks && !networks.length && <p className="col-span-3 py-2 text-sm text-neutral-500">Loading networks...</p>}
              {networks.map((network, index) => {
                const id = getNetworkId(network);
                const selected = String(id) === String(networkId);

                return (
                  <button
                    key={String(id ?? index)}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => selectNetwork(id)}
                    className={`h-11 rounded-xl border px-2 text-sm font-semibold ${selected ? networkTheme(network).tab : "border-neutral-300 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"}`}
                  >
                    {networkLabel(network)}
                  </button>
                );
              })}
            </div>

            <h3 className="mb-3 mt-6 text-base font-bold">Data Packages</h3>

            {loadingPackages ? (
              <p className="py-6 text-center text-sm text-neutral-500">Loading packages...</p>
            ) : filteredPackages.length === 0 ? (
              <p className="rounded-xl bg-neutral-50 px-4 py-8 text-center text-sm text-amber-600 dark:bg-neutral-900 dark:text-amber-400">No packages are available for this network.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {filteredPackages.map((item, index) => {
                  const key = getPackageKey(item, index);
                  const theme = networkTheme(networks.find((n) => String(getNetworkId(n)) === String(networkId)) ?? item);

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => choosePackage(key)}
                      className={`rounded-xl border p-3 text-center shadow-sm ${theme.card}`}
                    >
                      <span className="block text-base font-bold">{getPackageName(item)}</span>
                      <span className={`mt-1 block text-sm font-semibold ${theme.price}`}>{formatMoney(getPrice(item), getCurrency(item))}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ================= BUY DATA TAB: PURCHASE FORM ================= */}
        {activeTab === "buy" && buyView === "form" && (
          <form onSubmit={handlePreview} className="space-y-5">
            <section className={cardClass}>
              <h2 className="mb-4 text-base font-bold">Purchase Details</h2>

              {/* one card: stacked on mobile, two columns inside the same card on desktop */}
              <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-10">
              <div className="min-w-0">
              <label htmlFor="data-recipient" className="mb-2 block text-sm font-medium">Recipient Phone Number</label>
              <input id="data-recipient" name="recipient_msisdn" type="tel" inputMode="tel" autoComplete="tel" required value={phone} onChange={(e) => setPhone(normalizePhone(e.target.value))} pattern="0[0-9]{9}" title="10 digits starting with 0" placeholder="e.g. 0551053716" className={inputClass} />

              <div className="mb-2 mt-4 flex items-center justify-between">
                <span className="text-sm font-medium">Share Bundle</span>
                <button type="button" onClick={() => { clearNotices(); setBuyView("packages"); }} className="text-sm font-semibold text-orange-600 hover:underline dark:text-orange-400">Change</button>
              </div>
              {selectedPackage ? (
                <div className={`rounded-xl border px-3 py-2 ${selectedTheme.card.replace(/hover:\S+/g, "").trim()}`}>
                  <p className="text-sm font-semibold">
                    {getNetworkName(networks.find((n) => String(getNetworkId(n)) === String(networkId)) ?? selectedPackage)} · {getPackageName(selectedPackage)}
                  </p>
                  <p className={`text-base font-bold ${selectedTheme.price}`}>{formatMoney(getPrice(selectedPackage), getCurrency(selectedPackage))}</p>
                </div>
              ) : (
                <p className="text-sm text-amber-600 dark:text-amber-400">No package selected. Go back and choose a package.</p>
              )}
              </div>

              <div className="min-w-0">
              <p className="mb-2 mt-4 text-sm font-medium lg:mt-0">Pay with</p>
              <div role="radiogroup" aria-label="Payment method" className="rounded-xl bg-neutral-100 px-3 py-2 dark:bg-neutral-900">
                {Object.entries(PAY).map(([key, { label }]) => {
                  const selected = payMethod === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => { setPayMethod(key); clearNotices(); }}
                      className="flex w-full items-center justify-between text-sm leading-[22px]"
                    >
                      <span className="flex items-center gap-2">
                        <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${selected ? "border-orange-500" : "border-neutral-400"}`}>
                          {selected && <span className="h-2 w-2 rounded-full bg-orange-500" />}
                        </span>
                        <span className={selected ? "font-semibold text-orange-600 dark:text-orange-400" : ""}>{label}</span>
                      </span>
                      <span className={`font-semibold ${selected ? "text-green-600" : "text-neutral-600 dark:text-neutral-300"}`}>{balances[key].toFixed(2)} {label}</span>
                    </button>
                  );
                })}
              </div>

              {hasPrice && (
                <div className="mt-3 rounded-xl border border-neutral-200 p-3 text-sm dark:border-neutral-700">
                  <div className="flex justify-between"><span className="text-neutral-500">Price</span><span>{formatMoney(priceGhs, "GHS")}</span></div>
                  <div className="mt-1 flex justify-between font-semibold"><span>You pay</span><span>{costs[payMethod].toFixed(2)} {PAY[payMethod].label}</span></div>
                  <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">1 USWD = {GHSX_PER_USWD} GHSX</p>
                </div>
              )}

              {insufficient && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{insufficientMsg}</p>}
              </div>
              </div>

              <button type="submit" disabled={buying || loadingNetworks || loadingPackages || !selectedPackage || !hasPrice || insufficient || !isValidPhone(phone)} className={`mt-5 inline-flex h-11 w-full items-center justify-center rounded-xl border px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${selectedTheme.card}`}>
                {`Pay with ${PAY[payMethod].label}`}
              </button>

              <p className="mt-3 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                If a purchase is pending or the request times out, check its status before submitting another purchase.
              </p>
            </section>
          </form>
        )}

        {/* ---------- PREVIEW ---------- */}
        {activeTab === "buy" && buyView === "preview" && summary && (
          <section className={cardClass}>
            <h2 className="text-base font-bold">Confirm Purchase</h2>
            <p className="mb-4 mt-1 text-sm text-neutral-500 dark:text-neutral-400">Check the details below before you pay.</p>

            <dl className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 text-sm dark:divide-neutral-700 dark:border-neutral-700">
              {[
                ["Recipient", summary.recipient],
                ["Network", summary.networkName],
                ["Package", summary.packageName],
                ["Price", formatMoney(summary.priceGhs, "GHS")],
                ["Payment method", PAY[summary.method].label],
                ["You pay", `${summary.cost.toFixed(2)} ${PAY[summary.method].label}`],
                ["Balance after", `${summary.balanceAfter.toFixed(2)} ${PAY[summary.method].label}`],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 px-4 py-3">
                  <dt className="text-neutral-500">{label}</dt>
                  <dd className="break-all text-right font-medium">{value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => { clearNotices(); setBuyView("form"); }} disabled={buying} className={`${smallBtnClass} py-3 disabled:opacity-60`}>Edit</button>
              <button type="button" onClick={handleConfirm} disabled={buying} className={buttonClass}>{buying ? "Processing..." : "Confirm & Pay"}</button>
            </div>

            <p className="mt-3 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
              Your {PAY[summary.method].label} wallet is charged when you confirm. If the order fails, the payment is refunded.
            </p>
          </section>
        )}

        {/* ---------- DONE ---------- */}
        {activeTab === "buy" && buyView === "done" && summary && (
          <section className={`${cardClass} text-center`}>
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-lg font-bold">Purchase Submitted</h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{summary.packageName} {summary.networkName} for {summary.recipient}</p>
            <div className="mt-3 flex justify-center"><TransactionStatus status={summary.status} /></div>

            <dl className="mt-5 divide-y divide-neutral-200 rounded-xl border border-neutral-200 text-left text-sm dark:divide-neutral-700 dark:border-neutral-700">
              <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-neutral-500">Paid</dt><dd className="font-medium">{summary.cost.toFixed(2)} {PAY[summary.method].label}</dd></div>
              {summary.transactionId && <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-neutral-500">Order ID</dt><dd className="break-all text-right font-medium">{summary.transactionId}</dd></div>}
            </dl>

            <p className="mt-3 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">{summary.message}</p>

            <div className="mt-5">
              <button type="button" onClick={startNewPurchase} className={`${buttonClass} w-full`}>Done</button>
            </div>
          </section>
        )}

        {/* ================= TRANSACTION STATUS TAB ================= */}
        {activeTab === "status" && (
          <section className={cardClass}>
            <h2 className="text-base font-bold">Check Transaction Status</h2>
            <p className="mb-4 mt-1 text-sm text-neutral-500 dark:text-neutral-400">Enter the phone number the data was bought for.</p>

            <form onSubmit={handleCheckStatus} className="space-y-3">
              <label htmlFor="data-status-phone" className="block text-sm font-medium">Recipient Phone Number</label>
              <input id="data-status-phone" name="recipient_msisdn" type="tel" inputMode="tel" autoComplete="tel" required value={statusPhone} onChange={(e) => setStatusPhone(normalizePhone(e.target.value))} pattern="0[0-9]{9}" title="10 digits starting with 0" placeholder="e.g. 0551053716" className={inputClass} />
              <button type="submit" disabled={checkingStatus || !isValidPhone(statusPhone)} className={`${buttonClass} w-full`}>
                {checkingStatus ? "Checking..." : "Check Status"}
              </button>
            </form>

            {result && (
              <div className="mt-5 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-bold">Transaction Result</h3>
                  <TransactionStatus status={getStatus(result)} />
                </div>

                <dl className="mt-4 space-y-3 text-sm">
                  <Field label="Order ID" className="break-all font-medium">{getTransactionId(result) || "—"}</Field>
                  {getRecipient(result) && <Field label="Recipient">{getRecipient(result)}</Field>}
                  {getNetwork(result) !== "—" && <Field label="Network">{getNetwork(result)}</Field>}
                  {getAmount(result) != null && <Field label="Amount">{formatMoney(getAmount(result), result.currency || "GHS")}</Field>}
                  {(result.providerMessage || result.message) && <Field label="Message">{result.providerMessage || result.message}</Field>}
                  {getDate(result) && <Field label="Date">{formatDate(getDate(result))}</Field>}
                </dl>
              </div>
            )}
          </section>
        )}

        {/* ================= PURCHASE HISTORY TAB ================= */}
        {activeTab === "history" && (
          <section className={cardClass}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-bold">Purchase History</h2>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">View your previous data purchases.</p>
              </div>
              <button type="button" onClick={() => loadHistory()} disabled={loadingHistory} className={`${smallBtnClass} disabled:opacity-60`}>
                {loadingHistory ? "Loading..." : "Refresh"}
              </button>
            </div>

            {loadingHistory && !history.length ? (
              <p className="py-6 text-center text-sm text-neutral-500">Loading purchase history...</p>
            ) : history.length === 0 ? (
              <div className="rounded-xl bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500 dark:bg-neutral-900">No purchases found.</div>
            ) : (
              <div className="space-y-3">
                {history.map((item, index) => {
                  const id = getTransactionId(item);

                  return (
                    <article key={String(id || item._id || index)} className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-700">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold">{getPackageName({ ...item, sharedBundle: getBundle(item) })}</p>
                          <p className="mt-1 break-all text-xs text-neutral-500">
                            {id ? `Order ID: ${id}` : "Order ID unavailable"}
                          </p>
                        </div>
                        <TransactionStatus status={getStatus(item)} />
                      </div>

                      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <Field label="Recipient" small className="break-words">{getRecipient(item) || "—"}</Field>
                        <Field label="Network" small>{item.networkName || (networks.find((n) => String(getNetworkId(n)) === String(item.networkId)) ? getNetworkName(networks.find((n) => String(getNetworkId(n)) === String(item.networkId))) : "—")}</Field>
                        <Field label="Amount" small>{formatMoney(getAmount(item), getCurrency(item))}</Field>
                        <Field label="Date" small>{formatDate(getDate(item))}</Field>
                      </dl>

                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default BuyData;